/* ═══════════════════════════════════════════════════════════
   CTX Hero Slider — JavaScript
   File: assets/ctx-hero-slider.js
   ═══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  class CtxHeroSlider {
    constructor(section) {
      this.section     = section;
      this.sectionId   = section.dataset.sectionId;
      this.slides      = Array.from(section.querySelectorAll('.ctx-hero__slide'));
      this.dots        = Array.from(section.querySelectorAll('.ctx-hero__dot'));
      this.autoplay    = section.dataset.autoplay === 'true';
      this.interval    = parseInt(section.dataset.interval, 10) || 5000;
      this.current     = 0;
      this.timer       = null;
      this.touchStartX = 0;

      if (this.slides.length < 2) return; // single slide — no controls needed

      this._bindDots();
      this._bindTouch();
      this._bindKeyboard();

      if (this.autoplay) this._startTimer();

      // Pause on tab blur, resume on focus
      document.addEventListener('visibilitychange', () => {
        document.hidden ? this._stopTimer() : (this.autoplay && this._startTimer());
      });
    }

    /* ── Go to slide ── */
    goTo(idx) {
      if (idx === this.current) return;

      this.slides[this.current].classList.remove('active');
      this.slides[this.current].setAttribute('aria-hidden', 'true');
      if (this.dots[this.current]) {
        this.dots[this.current].classList.remove('active');
        this.dots[this.current].setAttribute('aria-selected', 'false');
      }

      this.current = (idx + this.slides.length) % this.slides.length;

      this.slides[this.current].classList.add('active');
      this.slides[this.current].setAttribute('aria-hidden', 'false');
      if (this.dots[this.current]) {
        this.dots[this.current].classList.add('active');
        this.dots[this.current].setAttribute('aria-selected', 'true');
      }
    }

    next() { this.goTo(this.current + 1); }
    prev() { this.goTo(this.current - 1); }

    /* ── Autoplay ── */
    _startTimer() {
      this._stopTimer();
      this.timer = setInterval(() => this.next(), this.interval);
    }
    _stopTimer() {
      clearInterval(this.timer);
      this.timer = null;
    }

    /* ── Dot navigation ── */
    _bindDots() {
      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          this._stopTimer();
          this.goTo(parseInt(dot.dataset.slide, 10));
          if (this.autoplay) this._startTimer();
        });
      });
    }

    /* ── Touch swipe ── */
    _bindTouch() {
      this.section.addEventListener('touchstart', (e) => {
        this.touchStartX = e.touches[0].clientX;
        this._swiped = false;
      }, { passive: true });

      this.section.addEventListener('touchend', (e) => {
        const diff = this.touchStartX - e.changedTouches[0].clientX;

        /*
          A slide whose buttons are hidden can carry a link covering the whole
          banner, and a swipe ends with a click on whatever is under the finger.
          Without this, every swipe on such a slide would navigate away instead
          of moving to the next slide.

          The threshold is deliberately lower than the 48px that triggers the
          swipe: a drag of 10-47px is not a swipe, but it is not a tap either,
          and firing the link on it feels like a misclick.
        */
        if (Math.abs(diff) > 10) this._swiped = true;

        if (Math.abs(diff) > 48) {
          this._stopTimer();
          diff > 0 ? this.next() : this.prev();
          if (this.autoplay) this._startTimer();
        }
      });

      /*
        Capture phase, so this runs before the browser follows the link. The
        flag is cleared on the next touchstart, so a genuine tap straight after
        a swipe still works.
      */
      this.section.addEventListener('click', (e) => {
        if (!this._swiped) return;
        const link = e.target.closest && e.target.closest('[data-ctx-hero-slide-link]');
        if (link) { e.preventDefault(); e.stopPropagation(); }
      }, true);
    }

    /* ── Keyboard navigation (when section is focused) ── */
    _bindKeyboard() {
      this.section.setAttribute('tabindex', '0');
      this.section.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { this._stopTimer(); this.next(); if (this.autoplay) this._startTimer(); }
        if (e.key === 'ArrowLeft')  { this._stopTimer(); this.prev(); if (this.autoplay) this._startTimer(); }
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════
     Keep hero copy clear of the fixed header

     The header is position:fixed, so it floats over the top of the hero, but
     the hero centres its copy in the full section height knowing nothing about
     it. Give it a tall headline that wraps to three lines, or a short browser
     window, and the centred block grows until its first line sits underneath
     the nav.

     This cannot be a fixed number in the stylesheet. The header sits at a
     constant offset from the VIEWPORT, while the hero's own top moves - it
     starts below the announcement bar, and a visitor can dismiss that bar and
     shift the hero upward, at which point the header covers MORE of it. So
     measure where the header actually ends and reserve exactly that much.

     The CSS consumes this as padding-top, and the copy uses auto margins so it
     centres in the space that's left and never rides back up into the strip.
  ═══════════════════════════════════════════════════════════ */
  const HEADER_GAP = 28;   // breathing room between the nav and the headline

  function findFixedHeader() {
    const candidates = document.querySelectorAll(
      'header, .section-header, #shopify-section-header, .custom-header, .site-header'
    );
    let best = null;
    candidates.forEach(el => {
      const cs = getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
      const r = el.getBoundingClientRect();
      if (r.height < 24) return;
      if (!best || r.bottom > best.bottom) best = { el, bottom: r.bottom };
    });
    return best;
  }

  function reserveHeaderSpace() {
    const header = findFixedHeader();
    document.querySelectorAll('.ctx-hero').forEach(hero => {
      let clear = 0;
      if (header) {
        /* Add scrollY so the sum is the hero's position in the document, making
           the result the same whether or not the page is currently scrolled. */
        const heroTopInDoc = hero.getBoundingClientRect().top + window.scrollY;
        clear = Math.max(0, Math.round(header.bottom - heroTopInDoc + HEADER_GAP));
      }
      const next = clear + 'px';
      /* Only write on change. This runs from a ResizeObserver on <body>, and
         changing the padding changes the body height - writing unconditionally
         would feed the observer its own output. */
      if (hero.style.getPropertyValue('--ctx-hero-top-clear') !== next) {
        hero.style.setProperty('--ctx-hero-top-clear', next);
      }
    });
  }

  /* ── Boot: initialize on DOMContentLoaded + Shopify section events ── */
  function initSections() {
    document.querySelectorAll('[data-section-id]').forEach(el => {
      if (el.querySelector('.ctx-hero__slides') && !el._ctxSliderInit) {
        el._ctxSliderInit = new CtxHeroSlider(el);
      }
    });
    /* Deliberately outside the class: CtxHeroSlider bails out early on
       single-slide heroes, and those need the clearance just as much. */
    reserveHeaderSpace();
  }

  window.addEventListener('resize', reserveHeaderSpace);
  window.addEventListener('load', reserveHeaderSpace);
  if (window.ResizeObserver) {
    /* Catches the announcement bar being dismissed, which moves the hero up
       without firing a resize event. */
    new ResizeObserver(reserveHeaderSpace).observe(document.body);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSections);
  } else {
    initSections();
  }

  /* ── Shopify Theme Editor — live reload support ── */
  document.addEventListener('shopify:section:load',   initSections);
  document.addEventListener('shopify:section:reorder', initSections);
  document.addEventListener('shopify:block:select', (e) => {
    const section = e.target.closest('[data-section-id]');
    if (section && section._ctxSliderInit) {
      const blockIndex = Array.from(section.querySelectorAll('.ctx-hero__slide'))
        .indexOf(e.target);
      if (blockIndex >= 0) {
        section._ctxSliderInit._stopTimer();
        section._ctxSliderInit.goTo(blockIndex);
      }
    }
  });

})();
